import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase automatically exchanges the code in the URL when detectSessionInUrl: true.
    // We just listen for the resulting auth state change and redirect accordingly.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate('/app', { replace: true });
      } else if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true });
      }
    });

    // Also check if a session already exists (handles page reload at /auth/callback)
    supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
      if (sessionError) {
        setError('Authentication failed. Please try signing in again.');
        return;
      }
      if (session) {
        navigate('/app', { replace: true });
      }
    });

    // Fallback: if nothing happens in 10s, something went wrong
    const timeout = setTimeout(() => {
      setError('Authentication timed out. Please try signing in again.');
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="card p-8 max-w-md w-full text-center space-y-4">
          <div className="text-bad">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold">Authentication Error</h2>
          <p className="text-muted-foreground text-sm">{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="button button-primary w-full"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
          <div className="absolute inset-0 rounded-full border-4 border-t-primary animate-spin" />
        </div>
        <p className="text-muted-foreground text-sm">Completing sign in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
